import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, ThumbsDown, ThumbsUp, X } from "lucide-react";
import TexteAssistant from "./TexteAssistant";

/* L'assistant du site public. Choisi dans la console (Assistant IA → Consignes → Site web) :
   en ligne ou en pause. FastBots n'est plus chargé (abonnement arrêté le 13.10.2026) : sans
   réponse du serveur, ou en pause, le site n'affiche simplement aucune bulle.

   La conversation suit la visite (sessionStorage) : elle reste ouverte d'une page à
   l'autre. Fermée pendant la visite, la bulle d'accueil ne revient pas ; à la visite
   suivante, elle est de nouveau là. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chatbot`;
const CLE = "assistant_1000arts";
const FERMEE = "chat_bulle_fermee";
const LIEU = "assistant_lieu";
// La ville et le canton donnés par Netlify (fonction Edge « lieu-visiteur », voir
// frontend/netlify/edge-functions/lieu-visiteur.js) : allumés, sauf REACT_APP_GEO_NETLIFY=false.
// Sans la fonction (ordinateur de développement), la réponse n'est pas du JSON : rien n'est joint.
const LIEU_NETLIFY = process.env.REACT_APP_GEO_NETLIFY !== "false";

const lire = (cle, defaut) => {
  try { return JSON.parse(sessionStorage.getItem(cle)) ?? defaut; } catch { return defaut; }
};
const ecrire = (cle, valeur) => {
  try { sessionStorage.setItem(cle, JSON.stringify(valeur)); } catch { /* navigation privée */ }
};

/* D'où écrit le visiteur, pour les statistiques de l'école (« D'où viennent les
   conversations ») : le fuseau horaire et la langue de l'appareil (Europe/Zurich → Suisse) et,
   si la fonction Edge de Netlify est allumée, la ville et le canton que Netlify déduit lui-même
   de la connexion. Jamais l'adresse IP. Joint à la première question seulement. */
async function lieuDuVisiteur() {
  const lieu = { langue: (navigator.language || "").slice(0, 20) };
  try { lieu.fuseau = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* vieux navigateur */ }
  if (LIEU_NETLIFY) {
    let geo = lire(LIEU, null);
    if (!geo) {
      geo = {};
      try {
        const arret = new AbortController();
        const minuterie = setTimeout(() => arret.abort(), 1500);
        const r = await fetch("/lieu-visiteur", { signal: arret.signal, cache: "no-store" });
        clearTimeout(minuterie);
        const d = r.ok && (r.headers.get("content-type") || "").includes("json") ? await r.json() : null;
        if (d && d.pays) geo = { ville: d.ville || "", region: d.region || "", pays: d.pays };
      } catch { /* pas de lieu : le fuseau suffit */ }
      ecrire(LIEU, geo);
    }
    if (geo.pays) lieu.lieu = geo;
  }
  return lieu;
}

const Logo = ({ taille = 36 }) => (
  <span className="rounded-full bg-white flex items-center justify-center shrink-0 overflow-hidden"
    style={{ width: taille, height: taille, boxShadow: "0 0 0 2px #00C6CE" }}>
    <img src="/1000arts-logo.png" alt="" style={{ width: taille * 0.78, height: taille * 0.78, objectFit: "contain" }} />
  </span>
);

const SUGGESTIONS = ["Quel cours pour mon enfant ?", "Les horaires du mercredi", "Les tarifs",
  "Les prochaines portes ouvertes", "Comment s'inscrire ?"];

export default function AssistantSite() {
  const [config, setConfig] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [bulle, setBulle] = useState(() => !lire(FERMEE, false));
  const [etat, setEtat] = useState(() => lire(CLE, { id: "", messages: [], avis: {} }));
  const [question, setQuestion] = useState("");
  const [enCours, setEnCours] = useState(false);
  const fond = useRef(null);
  const champ = useRef(null);
  const lieu = useRef(null);

  useEffect(() => {
    let vivant = true;
    fetch(`${API}/config`).then((r) => (r.ok ? r.json() : { fournisseur: "aucun" }))
      .catch(() => ({ fournisseur: "aucun" }))
      .then((c) => { if (vivant) setConfig(c); });
    return () => { vivant = false; };
  }, []);
  useEffect(() => { ecrire(CLE, etat); }, [etat]);
  useEffect(() => { fond.current?.scrollIntoView({ block: "end" }); }, [etat.messages, ouvert, enCours]);
  // Le lieu est préparé dès l'ouverture : la première question ne l'attend pas.
  useEffect(() => { if (ouvert && !etat.id && !lieu.current) lieu.current = lieuDuVisiteur(); }, [ouvert, etat.id]);
  useEffect(() => {
    if (!ouvert) return undefined;
    if (window.matchMedia?.("(min-width: 640px)").matches) champ.current?.focus();
    const echap = (e) => { if (e.key === "Escape") setOuvert(false); };
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [ouvert]);

  if (!config || config.fournisseur !== "maison") return null;

  const fermerBulle = () => { setBulle(false); ecrire(FERMEE, true); };
  const envoyer = async (texte) => {
    const q = (typeof texte === "string" ? texte : question).trim();
    if (!q || enCours) return;
    setQuestion("");
    setEnCours(true);
    const avant = etat;
    setEtat({ ...avant, messages: [...avant.messages, { role: "user", texte: q }] });
    try {
      const ou = avant.id ? {} : await (lieu.current || lieuDuVisiteur()).catch(() => ({}));
      const r = await fetch(`${API}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, conversation_id: avant.id, page: window.location.pathname, ...ou }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "L'assistant est momentanément indisponible.");
      setEtat({ ...avant, id: d.conversation_id,
                messages: [...avant.messages, { role: "user", texte: q },
                           { role: "assistant", texte: d.reponse, index: d.index }] });
    } catch (e) {
      setEtat({ ...avant, messages: [...avant.messages, { role: "user", texte: q },
                                     { role: "assistant", texte: e.message, erreur: true }] });
    } finally {
      setEnCours(false);
    }
  };
  const donnerAvis = (m, avis) => {
    if (!etat.id || m.index === undefined) return;
    setEtat({ ...etat, avis: { ...etat.avis, [m.index]: avis } });
    fetch(`${API}/avis`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ conversation_id: etat.id, index: m.index, avis }) }).catch(() => {});
  };

  const suggestions = (config.suggestions?.length ? config.suggestions : SUGGESTIONS).slice(0, 6);
  // La colonne fixe (bulle + bouton rond) est un rectangle invisible bien plus large que le
  // bouton : elle avalait les taps de ce qui est dessous, dont « Retour à mon application »
  // (seuls 43 px sur 195 répondaient, école 09.10.2026). Elle laisse donc passer le doigt ;
  // seuls ses vrais éléments (fenêtre, bulle, bouton rond) le reçoivent. Dans l'app
  // installée, index.css (body.depuis-app) masque la bulle et remonte l'assistant au-dessus
  // du bouton de retour.
  return (
    <div className="assistant-site pointer-events-none fixed z-[60] right-4 bottom-4 md:right-6 md:bottom-6 flex flex-col items-end gap-3"
      style={{ fontFamily: "inherit" }} data-testid="assistant-site">
      <style>{`
        @keyframes assistant-point { 0%, 80%, 100% { opacity: .25; transform: translateY(0) }
                                     40% { opacity: 1; transform: translateY(-3px) } }
        @keyframes assistant-entree { from { opacity: 0; transform: translateY(12px) scale(.98) }
                                      to { opacity: 1; transform: none } }
        @media (max-width: 639px) {
          .assistant-panneau { position: fixed; inset: 0; width: 100% !important; height: 100% !important;
                               border-radius: 0 !important; z-index: 70; }
        }
      `}</style>
      {ouvert && (
        <div className="assistant-panneau pointer-events-auto bg-white rounded-3xl shadow-2xl border flex flex-col overflow-hidden"
          style={{ borderColor: "#E5E7EB", width: "min(390px, calc(100vw - 2rem))",
                   height: "min(600px, calc(100dvh - 7rem))", animation: "assistant-entree .2s ease-out" }}
          role="dialog" aria-label="Assistant 1000 ARTS">
          <div className="flex items-center gap-3 px-4 py-3.5" style={{ background: "#0F1B2D", color: "#fff" }}>
            <Logo taille={40} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] leading-tight">Assistant 1000 ARTS</div>
              <div className="text-[11px] flex items-center gap-1.5 mt-0.5" style={{ color: "rgba(255,255,255,.7)" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#22C55E" }} />
                En ligne · répond à partir du site
              </div>
            </div>
            <button onClick={() => setOuvert(false)} aria-label="Fermer" data-testid="assistant-fermer"
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm" style={{ background: "#F8FAFC" }}>
            <div className="flex gap-2 items-end">
              <Logo taille={26} />
              <div className="rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[85%] bg-white border" style={{ borderColor: "#E5E7EB" }}>{config.accueil}</div>
            </div>
            {etat.messages.length === 0 && (
              <div className="flex flex-wrap gap-2 pl-8" data-testid="assistant-suggestions">
                {suggestions.map((sug) => (
                  <button key={sug} onClick={() => envoyer(sug)}
                    className="text-xs px-3 py-1.5 rounded-full border bg-white transition-colors hover:bg-[#E6FAFB]"
                    style={{ borderColor: "#00C6CE", color: "#007E85" }}>{sug}</button>
                ))}
              </div>
            )}
            {etat.messages.map((m, i) => (m.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="rounded-2xl rounded-br-md px-4 py-2.5 max-w-[85%] whitespace-pre-wrap"
                  style={{ background: "#00C6CE", color: "#04202A" }}>{m.texte}</div>
              </div>
            ) : (
              <div key={i} className="flex gap-2 items-end">
                <Logo taille={26} />
                <div className="rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[85%] border space-y-0.5"
                  style={{ background: m.erreur ? "#FEF2F2" : "#fff", borderColor: m.erreur ? "#FECACA" : "#E5E7EB" }}>
                  <TexteAssistant texte={m.texte} />
                  {!m.erreur && (
                    <div className="flex gap-2.5 pt-1.5 justify-end">
                      {[[1, ThumbsUp, "Réponse utile"], [-1, ThumbsDown, "Réponse pas utile"]].map(([v, Icone, label]) => (
                        <button key={v} onClick={() => donnerAvis(m, v)} aria-label={label} title={label}
                          style={{ opacity: etat.avis[m.index] === v ? 1 : 0.3, color: etat.avis[m.index] === v ? "#007E85" : undefined }}>
                          <Icone size={13} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )))}
            {enCours && (
              <div className="flex gap-2 items-end" aria-label="L'assistant écrit">
                <Logo taille={26} />
                <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white border flex gap-1" style={{ borderColor: "#E5E7EB" }}>
                  {[0, 1, 2].map((k) => (
                    <span key={k} className="w-1.5 h-1.5 rounded-full"
                      style={{ background: "#00C6CE", animation: `assistant-point 1.2s ${k * 0.15}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={fond} />
          </div>
          <div className="border-t px-3 pt-3 pb-2 bg-white" style={{ borderColor: "#E5E7EB" }}>
            <div className="flex gap-2">
              <input ref={champ} className="flex-1 rounded-full border px-4 py-2.5 text-sm outline-none focus:border-[#00C6CE]"
                style={{ borderColor: "#E5E7EB", fontSize: 16 }} value={question} maxLength={800}
                placeholder="Pose ta question…" onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && envoyer()} data-testid="assistant-question" />
              <button onClick={() => envoyer()} disabled={enCours || !question.trim()} aria-label="Envoyer"
                className="rounded-full w-11 h-11 flex items-center justify-center disabled:opacity-40 shrink-0"
                style={{ background: "#00C6CE", color: "#04202A" }} data-testid="assistant-envoyer">
                <Send size={17} />
              </button>
            </div>
            <div className="text-[10px] text-center mt-1.5" style={{ color: "#94A3B8" }}>
              Assistant automatique : il peut se tromper. Questions personnelles : info1000arts@gmail.com
            </div>
          </div>
        </div>
      )}
      {!ouvert && bulle && (
        <div className="assistant-bulle pointer-events-auto bg-white rounded-2xl rounded-br-md shadow-xl border px-4 py-3 text-sm max-w-[270px] relative"
          style={{ borderColor: "#E5E7EB", animation: "assistant-entree .3s ease-out" }} data-testid="assistant-bulle">
          <button onClick={fermerBulle} aria-label="Fermer le message"
            className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-white border shadow flex items-center justify-center"
            style={{ borderColor: "#E5E7EB" }}><X size={12} /></button>
          <button className="text-left" onClick={() => { setOuvert(true); fermerBulle(); }}>{config.accueil}</button>
        </div>
      )}
      {!ouvert && (
        <button onClick={() => { setOuvert(true); fermerBulle(); }} aria-label="Ouvrir l'assistant 1000 ARTS"
          className="pointer-events-auto rounded-full w-16 h-16 shadow-xl flex items-center justify-center transition-transform hover:-translate-y-0.5 relative bg-white"
          style={{ boxShadow: "0 0 0 3px #00C6CE, 0 10px 25px rgba(15,27,45,.25)" }} data-testid="assistant-ouvrir">
          <img src="/1000arts-logo.png" alt="" className="w-11 h-11 object-contain" />
          <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: "#0F1B2D", color: "#7DE8ED" }}><MessageCircle size={13} /></span>
        </button>
      )}
    </div>
  );
}
